import { useCallback, useState } from "react";
import { useLocation } from "react-router-dom";
import NotesField from "../forms/NotesField";
import { useSellerState } from "../../js/state/sellerState";
import { useAutosave } from "../../js/hooks/useAutosave";
import { useGuestMode } from "../../js/hooks/useGuestMode";
import { SELLER_FRAME_STEPS } from "../../js/utils/sellerSteps";
import { toTitleCase } from "../../js/utils/titleCase";

const FRAME_STEPS = [{ path: "/seller/home", label: "Seller Home", key: "home" }, ...SELLER_FRAME_STEPS];

export default function SellerNotesPanel() {
  const { pathname } = useLocation();
  const { seller, updatePortfolio } = useSellerState();
  const { canPersist } = useGuestMode();
  const current = FRAME_STEPS.find((step) => step.path === pathname) || FRAME_STEPS[0];
  const [pageNotes, setPageNotes] = useState(() => ({ ...(seller.portfolio.pageNotes || {}) }));
  const [general, setGeneral] = useState(() => ({
    forAgent: seller.portfolio.notes?.forAgent || "",
    showing: seller.portfolio.notes?.showing || "",
    disclosures: seller.portfolio.notes?.disclosures || "",
  }));

  const persist = useCallback(
    (next) => {
      if (!canPersist) return;
      updatePortfolio("pageNotes", next.pageNotes);
      updatePortfolio("notes", next.general);
    },
    [canPersist, updatePortfolio]
  );

  useAutosave(canPersist ? { pageNotes, general } : null, persist);

  return (
    <div className="sidebar-panel">
      <h3 className="sidebar-panel__title">Notes · {toTitleCase(current.label)}</h3>
      <p className="muted sidebar-panel__lead sidebar-guide">
        Page notes follow the form on the right. Tips and readiness stay on their own tabs.
      </p>
      <NotesField
        className="sidebar-page-note"
        label={`${toTitleCase(current.label)} notes for my agent`}
        name={current.key}
        value={pageNotes[current.key] || ""}
        onChange={(event) => setPageNotes((currentNotes) => ({ ...currentNotes, [current.key]: event.target.value }))}
        placeholder="Condition, access, or context for this part of the portfolio."
        rows={6}
      />
      <ol className="sidebar-note-index">
        {FRAME_STEPS.map((step) => (
          <li key={step.key} className={step.key === current.key ? "is-current" : ""}>
            <span>{toTitleCase(step.label)}</span>
            <small>{pageNotes[step.key] ? "Noted" : "Open"}</small>
          </li>
        ))}
      </ol>
      <details className="sidebar-panel__extra">
        <summary>General notes</summary>
        <NotesField
          label="For my agent"
          name="forAgent"
          value={general.forAgent}
          onChange={(event) => setGeneral((currentNotes) => ({ ...currentNotes, forAgent: event.target.value }))}
          rows={3}
        />
        <NotesField
          label="Showing notes"
          name="showing"
          value={general.showing}
          onChange={(event) => setGeneral((currentNotes) => ({ ...currentNotes, showing: event.target.value }))}
          rows={3}
        />
        <NotesField
          label="Disclosure notes"
          name="disclosures"
          value={general.disclosures}
          onChange={(event) => setGeneral((currentNotes) => ({ ...currentNotes, disclosures: event.target.value }))}
          rows={3}
        />
      </details>
    </div>
  );
}
